import * as z from 'zod'
import { postgrestFrom, runPostgrest, runPostgrestPage } from '@/api/postgrest/client'
import {
  producttypeNameRowSchema,
  producttypeSalesRowSchema,
  producttypeTrafficRowSchema,
  producttypeKeywordRowSchema,
} from '@/api/keywordAnalysis/schemas'
import type {
  KeywordAnalysisPage,
  KeywordAssignment,
  KeywordProducttype,
  KeywordType,
  ProducttypeSalesRow,
  ProducttypeTrafficRow,
  ProducttypeKeywordRow,
} from '@/api/keywordAnalysis/types'

const KEYWORD_QUERY_PAGE_SIZE = 500
const RANKING_QUERY_PAGE_SIZE = 500

export type KeywordAnalysisSort =
  | 'sales'
  | 'traffic'
  | 'secondary-traffic'
  | 'emotion-traffic'
  | 'longtail-traffic'
  | 'alias-traffic'

export type KeywordAnalysisView =
  'all' | 'without-primary' | 'without-secondary' | 'without-emotion' | 'without-alias'

const keywordTypeByTrafficSort: Readonly<
  Record<Exclude<KeywordAnalysisSort, 'sales' | 'alias-traffic'>, KeywordType>
> = {
  traffic: 0,
  'secondary-traffic': 1,
  'emotion-traffic': 2,
  'longtail-traffic': 3,
}

const trafficSortByMissingView: Readonly<
  Record<Exclude<KeywordAnalysisView, 'all'>, Exclude<KeywordAnalysisSort, 'sales'>>
> = {
  'without-primary': 'traffic',
  'without-secondary': 'secondary-traffic',
  'without-emotion': 'emotion-traffic',
  'without-alias': 'alias-traffic',
}

async function fetchAllSalesRows(signal?: AbortSignal): Promise<readonly ProducttypeSalesRow[]> {
  const rows: ProducttypeSalesRow[] = []

  for (let from = 0; ; from += RANKING_QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_sales')
      .select('producttypeKey:producttype_key,sales365:sales_365')
      .order('producttype_key', { ascending: true })
      .range(from, from + RANKING_QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(query, z.array(producttypeSalesRowSchema), 'producttype_sales')
    rows.push(...page)
    if (page.length < RANKING_QUERY_PAGE_SIZE) return rows
  }
}

async function fetchAllTrafficRows(
  sort: Exclude<KeywordAnalysisSort, 'sales'>,
  signal?: AbortSignal,
): Promise<readonly ProducttypeTrafficRow[]> {
  const rows: ProducttypeTrafficRow[] = []

  for (let from = 0; ; from += RANKING_QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_keywords')
      .select(
        'producttypeKey:producttype_key,...keywords!inner(traffic:search_volume_per_month_avg.sum())',
      )
      .order('producttype_key', { ascending: true })
      .range(from, from + RANKING_QUERY_PAGE_SIZE - 1)
    query =
      sort === 'alias-traffic'
        ? query.eq('keywords.is_alias', true)
        : query.eq('keywords.keyword_type', keywordTypeByTrafficSort[sort])
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeTrafficRowSchema),
      'producttype_keywords Traffic-Sortierung',
    )
    rows.push(...page)
    if (page.length < RANKING_QUERY_PAGE_SIZE) return rows
  }
}

async function fetchTrafficRankedSalesPage(
  page: number,
  pageSize: number,
  sort: KeywordAnalysisSort,
  view: KeywordAnalysisView,
  signal?: AbortSignal,
): Promise<{ readonly items: readonly ProducttypeSalesRow[]; readonly totalCount: number }> {
  const requestedTrafficSorts = new Set<Exclude<KeywordAnalysisSort, 'sales'>>()
  if (sort !== 'sales') requestedTrafficSorts.add(sort)
  if (view !== 'all') requestedTrafficSorts.add(trafficSortByMissingView[view])

  const [salesRows, trafficEntries] = await Promise.all([
    fetchAllSalesRows(signal),
    Promise.all(
      [...requestedTrafficSorts].map(async (trafficSort) => {
        const rows = await fetchAllTrafficRows(trafficSort, signal)
        return [trafficSort, rows] as const
      }),
    ),
  ])
  const trafficRowsBySort = new Map(trafficEntries)
  const presentKeys =
    view === 'all'
      ? null
      : new Set(
          (trafficRowsBySort.get(trafficSortByMissingView[view]) ?? []).map(
            (row) => row.producttypeKey,
          ),
        )
  const filteredRows =
    presentKeys === null
      ? salesRows
      : salesRows.filter((row) => !presentKeys.has(row.producttypeKey))
  const trafficByKey = new Map(
    sort === 'sales'
      ? []
      : (trafficRowsBySort.get(sort) ?? []).map(
          (row) => [row.producttypeKey, row.traffic] as const,
        ),
  )
  const rankedRows = [...filteredRows].sort((left, right) => {
    if (sort !== 'sales') {
      const trafficDifference =
        (trafficByKey.get(right.producttypeKey) ?? 0) - (trafficByKey.get(left.producttypeKey) ?? 0)
      if (trafficDifference !== 0) return trafficDifference
    }

    const salesDifference = right.sales365 - left.sales365
    return salesDifference !== 0
      ? salesDifference
      : left.producttypeKey.localeCompare(right.producttypeKey, 'de')
  })
  const from = (page - 1) * pageSize

  return {
    items: rankedRows.slice(from, from + pageSize),
    totalCount: rankedRows.length,
  }
}

async function fetchAllKeywordRows(
  producttypeKeys: readonly string[],
  signal?: AbortSignal,
): Promise<readonly ProducttypeKeywordRow[]> {
  const rows: ProducttypeKeywordRow[] = []

  for (let from = 0; ; from += KEYWORD_QUERY_PAGE_SIZE) {
    let query = postgrestFrom('producttype_keywords')
      .select(
        'producttypeKey:producttype_key,keyword:keywords!inner(keywordId:id,value,languageKey:language_key,keywordType:keyword_type,isAlias:is_alias,searchVolumePerMonthAvg:search_volume_per_month_avg)',
      )
      .in('producttype_key', producttypeKeys)
      .order('producttype_key', { ascending: true })
      .order('keyword_id', { ascending: true })
      .range(from, from + KEYWORD_QUERY_PAGE_SIZE - 1)
    if (signal !== undefined) query = query.abortSignal(signal)

    const page = await runPostgrest(
      query,
      z.array(producttypeKeywordRowSchema),
      'producttype_keywords',
    )
    rows.push(...page)

    if (page.length < KEYWORD_QUERY_PAGE_SIZE) return rows
  }
}

function keywordsByProducttype(
  rows: readonly ProducttypeKeywordRow[],
): ReadonlyMap<string, readonly KeywordAssignment[]> {
  const grouped = new Map<string, KeywordAssignment[]>()

  for (const row of rows) {
    const keywords = grouped.get(row.producttypeKey) ?? []
    keywords.push(row.keyword)
    grouped.set(row.producttypeKey, keywords)
  }

  for (const keywords of grouped.values()) {
    keywords.sort((left, right) => {
      const volumeDifference =
        (right.searchVolumePerMonthAvg ?? -1) - (left.searchVolumePerMonthAvg ?? -1)
      return volumeDifference !== 0 ? volumeDifference : left.value.localeCompare(right.value, 'de')
    })
  }

  return grouped
}

export async function fetchKeywordAnalysisPage(
  page: number,
  pageSize: number,
  sort: KeywordAnalysisSort,
  view: KeywordAnalysisView,
  signal?: AbortSignal,
): Promise<KeywordAnalysisPage> {
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const salesPage =
    sort !== 'sales' || view !== 'all'
      ? await fetchTrafficRankedSalesPage(page, pageSize, sort, view, signal)
      : await (async () => {
          let salesQuery = postgrestFrom('producttype_sales')
            .select('producttypeKey:producttype_key,sales365:sales_365', {
              count: 'planned',
            })
            .order('sales_365', { ascending: false, nullsFirst: false })
            .order('producttype_key', { ascending: true })
            .range(from, to)
          if (signal !== undefined) salesQuery = salesQuery.abortSignal(signal)

          return runPostgrestPage(
            salesQuery,
            z.array(producttypeSalesRowSchema),
            'producttype_sales',
          )
        })()

  const producttypeKeys = salesPage.items.map((row) => row.producttypeKey)
  if (producttypeKeys.length === 0) {
    return { items: [], totalCount: salesPage.totalCount }
  }

  let producttypeQuery = postgrestFrom('producttypes')
    .select('producttypeKey:producttypes_key,producttypeName:name')
    .in('producttypes_key', producttypeKeys)
  if (signal !== undefined) producttypeQuery = producttypeQuery.abortSignal(signal)

  const [producttypeRows, keywordRows] = await Promise.all([
    runPostgrest(producttypeQuery, z.array(producttypeNameRowSchema), 'producttypes'),
    fetchAllKeywordRows(producttypeKeys, signal),
  ])
  const namesByKey = new Map(
    producttypeRows.map((row) => [row.producttypeKey, row.producttypeName] as const),
  )
  const groupedKeywords = keywordsByProducttype(keywordRows)

  const items: readonly KeywordProducttype[] = salesPage.items.map((row) => ({
    producttypeKey: row.producttypeKey,
    producttypeName: namesByKey.get(row.producttypeKey) ?? row.producttypeKey,
    sales365: row.sales365,
    keywords: groupedKeywords.get(row.producttypeKey) ?? [],
  }))

  return { items, totalCount: salesPage.totalCount }
}

export async function fetchKeywordProducttype(
  producttypeKey: string,
  signal?: AbortSignal,
): Promise<KeywordProducttype | null> {
  let producttypeQuery = postgrestFrom('producttypes')
    .select('producttypeKey:producttypes_key,producttypeName:name')
    .eq('producttypes_key', producttypeKey)
    .limit(1)
  let salesQuery = postgrestFrom('producttype_sales')
    .select('producttypeKey:producttype_key,sales365:sales_365')
    .eq('producttype_key', producttypeKey)
    .limit(1)
  if (signal !== undefined) {
    producttypeQuery = producttypeQuery.abortSignal(signal)
    salesQuery = salesQuery.abortSignal(signal)
  }

  const [producttypeRows, salesRows, keywordRows] = await Promise.all([
    runPostgrest(producttypeQuery, z.array(producttypeNameRowSchema), 'producttypes'),
    runPostgrest(salesQuery, z.array(producttypeSalesRowSchema), 'producttype_sales'),
    fetchAllKeywordRows([producttypeKey], signal),
  ])
  const producttype = producttypeRows[0]
  if (producttype === undefined) return null

  return {
    producttypeKey: producttype.producttypeKey,
    producttypeName: producttype.producttypeName,
    sales365: salesRows[0]?.sales365 ?? 0,
    keywords: keywordsByProducttype(keywordRows).get(producttypeKey) ?? [],
  }
}
